import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SearchBar from "@/components/SearchBar";
import PropertyCard from "@/components/PropertyCard";
import EnquiryModal from "@/components/EnquiryModal";
import { api } from "@/lib/api";
import { isListingVisible } from "@/lib/featureFlags";
import { useLang } from "@/contexts/LanguageContext";
import { Sparkles, TrendingUp, ShieldCheck, ArrowRight, Hotel, BadgeCheck, BadgeIndianRupee } from "lucide-react";

const HERO_BG =
  "https://images.pexels.com/photos/28054849/pexels-photo-28054849.jpeg";

export default function Home() {
  const { t, lang } = useLang();
  const [searchParams] = useSearchParams();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enquiryFor, setEnquiryFor] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/properties");
        setAll((data || []).filter(isListingVisible));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const exclusiveSlugs = [
    "sparv-etereo-1",
    "ghd-velvet-vista",
    "aria-residency",
    "cloud-infinity-villas",
  ];
  const exclusiveProjects = useMemo(() => {
    const byProject = {};
    for (const p of all) {
      if (exclusiveSlugs.includes(p.project_slug)) {
        if (!byProject[p.project_slug]) byProject[p.project_slug] = [];
        byProject[p.project_slug].push(p);
      }
    }
    return exclusiveSlugs
      .filter((s) => byProject[s]?.length > 0)
      .map((slug) => {
        const units = byProject[slug];
        const first = units[0];
        const META = {
          "sparv-etereo-1": {
            display_name: "SPARV Etereo 1",
            tagline: "Siolim, North Goa · Premium 1 & 2 BHK Residences by SPARV",
            cover: "/projects/sparv-pool.jpg",
            chips: [
              "Assured Rental from ₹55,000/mo",
              "Book with Just 10%",
              "RERA Approved",
            ],
          },
          "ghd-velvet-vista": {
            display_name: "GHD Velvet Vista",
            tagline: "Porvorim, North Goa · Managed by Regenta Hotels & Resorts",
            cover:
              "https://ghdinfra.com/wp-content/uploads/2025/12/VELVET-1.webp",
            chips: [
              "Assured Rental ₹51,000/mo",
              "10% Assured Return",
              "Hotel-managed",
              "RERA PRGO05252368",
            ],
          },
          "aria-residency": {
            display_name: "Aria Residency",
            tagline: "Calangute, North Goa · 78 boutique residences by Eleva by Hiranandani",
            cover:
              "https://elevabyhiranandanigoa.com/Apartments/assets/images/banners/apt-desktop.webp",
            chips: [
              "Wellness Living",
              "Private Pool / Residence",
              "RERA PRGO04262718",
            ],
          },
          "cloud-infinity-villas": {
            display_name: "Cloud Infinity Villas",
            tagline: "Kadamba, Old Goa · 44 gated 3 & 4 BHK villas by Eleva by Hiranandani",
            cover:
              "https://elevabyhiranandanigoa.com/Villas/assets/images/banners/villa-desktop.webp",
            chips: [
              "Gated Villas · 44 Homes",
              "11,000+ sq.ft Clubhouse",
              "RERA PRGO03262696",
            ],
          },
        };
        return { slug, units, meta: META[slug], first };
      });
  }, [all]);

  const exclusiveIds = useMemo(
    () =>
      new Set(
        all
          .filter((p) => exclusiveSlugs.includes(p.project_slug))
          .map((p) => p.id)
      ),
    [all]
  );

  const featured = useMemo(
    () =>
      all
        .filter(
          (p) =>
            (p.is_featured || p.is_exclusive || p.is_assured_return) &&
            !exclusiveIds.has(p.id)
        )
        .slice(0, 6),
    [all, exclusiveIds]
  );
  const others = useMemo(
    () =>
      all.filter(
        (p) =>
          !(p.is_featured || p.is_exclusive || p.is_assured_return) &&
          !exclusiveIds.has(p.id)
      ),
    [all, exclusiveIds]
  );

  return (
    <div className="min-h-screen bg-white text-[#0B2545] font-sans">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-[#061528]/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#061528] via-[#0B2545]/90 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-32 md:pt-24 md:pb-44">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#C9A84C]/20 border border-[#C9A84C] text-[#E9D58F] text-xs uppercase tracking-[0.24em] font-bold">
              <Sparkles size={13} strokeWidth={3} />
              {t.hero.eyebrow}
            </div>
            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight font-bold">
              {t.hero.title}
            </h1>
            <p className="mt-5 text-base md:text-lg text-white max-w-xl leading-relaxed font-medium">
              {t.hero.subtitle}
            </p>
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-[#E6ECF5] font-semibold">
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck size={16} className="text-[#C9A84C]" strokeWidth={2.5} /> Vetted by wealth team
              </span>
              <span className="inline-flex items-center gap-1.5">
                <TrendingUp size={16} className="text-[#C9A84C]" strokeWidth={2.5} /> Up to 12% assured return
              </span>
            </div>
          </div>
        </div>

        {/* Overlapping search */}
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 md:-mt-24 pb-6 z-20">
          <SearchBar
            defaults={{
              listing_type: searchParams.get("listing_type") || "buy",
              city: searchParams.get("city") || "All",
              property_type: searchParams.get("property_type") || "All",
            }}
          />
        </div>
      </section>

      {/* Assured rental banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
        <a
          href="#exclusive-projects"
          data-testid="assured-rental-banner"
          className="group flex flex-col md:flex-row items-start md:items-center justify-between gap-5 rounded-2xl bg-[#0B2545] border-2 border-[#C9A84C] px-6 py-6 md:px-10 hover:shadow-[0_24px_50px_-24px_rgba(201,168,76,0.8)] transition-shadow"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 shrink-0 rounded-full bg-[#C9A84C] text-[#0B2545] flex items-center justify-center">
              <BadgeIndianRupee size={24} strokeWidth={2.5} />
            </div>
            <div className="font-serif text-2xl md:text-3xl font-bold text-[#C9A84C] leading-tight">
              {t.rental.banner_title}
              <span className="text-white"> — {t.rental.banner_from} </span>
              ₹51,000{t.rental.per_month_long}
            </div>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 h-11 px-5 rounded-md bg-[#C9A84C] text-[#0B2545] group-hover:bg-[#E9D58F] font-bold uppercase tracking-wider text-xs transition-colors">
            {t.rental.banner_cta}
            <ArrowRight size={14} strokeWidth={3} className="transition-transform group-hover:translate-x-1" />
          </span>
        </a>
      </section>

      {/* Richcube Exclusive Projects */}
      {exclusiveProjects.length > 0 && (
        <section
          id="exclusive-projects"
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-20 scroll-mt-24"
          data-testid="exclusive-projects"
        >
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#C9A84C] font-bold">
                Richcube Exclusive
              </div>
              <h2 className="mt-2 font-serif text-3xl md:text-4xl text-[#0B2545] rc-heading">
                Signature Projects
              </h2>
              <p className="mt-5 text-[#1F2A44] max-w-xl font-medium">
                Hand-picked Goa developments offered only through Richcube — complete with concierge-managed enquiries.
              </p>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            {exclusiveProjects.map(({ slug, meta: pm, first }) => (
              <Link
                key={slug}
                to={`/project/${slug}`}
                data-testid={`exclusive-tile-${slug}`}
                className="group relative overflow-hidden rounded-3xl border-2 border-[#C9A84C]/70 bg-[#0B2545] hover:border-[#C9A84C] hover:shadow-[0_28px_60px_-28px_rgba(201,168,76,0.7)] transition-all"
              >
                <div
                  className="absolute inset-0 bg-cover bg-center opacity-55 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700"
                  style={{ backgroundImage: `url(${pm.cover})` }}
                  aria-hidden
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#061528] via-[#0B2545]/80 to-[#0B2545]/30" />
                <div className="relative px-6 py-8 md:px-10 md:py-10 min-h-[360px] flex flex-col justify-end">
                  <div className="inline-flex self-start items-center gap-2 px-3 py-1 rounded-full bg-[#C9A84C]/20 border border-[#C9A84C] text-[#E9D58F] text-[10px] uppercase tracking-[0.24em] font-bold">
                    <Sparkles size={11} strokeWidth={3} />
                    Richcube Exclusive
                  </div>
                  <h3 className="mt-4 font-serif text-3xl md:text-4xl text-white leading-tight font-bold">
                    {pm.display_name}
                  </h3>
                  <p className="mt-2 text-[#E6ECF5] text-sm md:text-base font-semibold">
                    {pm.tagline}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {pm.chips.map((chip) => (
                      <span
                        key={chip}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white text-[11px] uppercase tracking-wider font-semibold backdrop-blur-sm"
                      >
                        <BadgeCheck size={11} strokeWidth={3} className="text-[#C9A84C]" />
                        {chip}
                      </span>
                    ))}
                  </div>
                  <div className="mt-6 inline-flex self-start items-center gap-1.5 h-10 px-5 rounded-md bg-[#C9A84C] text-[#0B2545] group-hover:bg-[#E9D58F] font-bold uppercase tracking-wider text-xs transition-colors">
                    View All Units
                    <ArrowRight size={14} strokeWidth={3} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-20" data-testid="featured-section">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#C9A84C] font-semibold">
              {t.badges.featured}
            </div>
            <h2 className="mt-2 font-serif text-3xl md:text-4xl text-[#0B2545] rc-heading">
              {t.sections.featured}
            </h2>
            <p className="mt-2 text-[#1F2A44] max-w-xl">{t.sections.featured_sub}</p>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-xl bg-[#F4F7FB] animate-pulse h-[380px]"
                  data-testid={`skeleton-${i}`}
                />
              ))
            : featured.map((p) => (
                <PropertyCard
                  key={p.id}
                  property={p}
                  featured
                  onEnquire={setEnquiryFor}
                />
              ))}
          {!loading && featured.length === 0 && (
            <p className="col-span-full text-[#1F2A44]">{t.sections.no_results}</p>
          )}
        </div>
      </section>

      {/* All listings */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20" data-testid="all-section">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#C9A84C] font-semibold">
            {t.cities.all}
          </div>
          <h2 className="mt-2 font-serif text-3xl md:text-4xl text-[#0B2545] rc-heading">
            {t.sections.all_listings}
          </h2>
          <p className="mt-2 text-[#1F2A44] max-w-xl">{t.sections.all_sub}</p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {others.map((p) => (
            <PropertyCard key={p.id} property={p} onEnquire={setEnquiryFor} />
          ))}
          {!loading && others.length === 0 && (
            <p className="col-span-full text-[#1F2A44]">
              {lang === "hi" ? "कोई अतिरिक्त लिस्टिंग नहीं।" : "No additional listings yet."}
            </p>
          )}
        </div>
      </section>

      {/* Trust strip */}
      <section className="mt-24 bg-[#0B2545]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-3 gap-10 text-white">
          <div>
            <div className="font-serif text-3xl text-[#C9A84C]">10 — 12%</div>
            <div className="mt-2 text-sm text-[#E6ECF5] uppercase tracking-[0.2em]">
              Assured annual return
            </div>
          </div>
          <div>
            <div className="font-serif text-3xl text-[#C9A84C]">Indore · Goa</div>
            <div className="mt-2 text-sm text-[#E6ECF5] uppercase tracking-[0.2em]">
              Focused Tier 2/3 corridors
            </div>
          </div>
          <div>
            <div className="font-serif text-3xl text-[#C9A84C]">24 × 7</div>
            <div className="mt-2 text-sm text-[#E6ECF5] uppercase tracking-[0.2em]">
              Dedicated wealth advisor
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <EnquiryModal
        property={enquiryFor}
        open={!!enquiryFor}
        onClose={() => setEnquiryFor(null)}
      />
    </div>
  );
}
